import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiGet, ApiClientError } from '@client/lib/api';
import { useSession } from '@client/lib/useSession';
import { todayIn, compareDates } from '@shared/time';
import {
  type CalendarResolution,
  rangeFor,
  gridFor,
  navigate,
  isOutsideMonth,
  formatRangeLabel,
} from '@shared/calendar';
import { formatCents } from '@shared/money';
import { type JobStatus } from '@shared/constants';
import styles from './CalendarPage.module.css';

/** A job row as returned by GET /api/v1/jobs. */
interface Job {
  id: string;
  title: string;
  status: string;
  scheduledDate: string;
  scheduledStartTime: string | null;
  priceCents: number;
  propertyId: string;
  customerId: string;
  organizationId: string;
  assignedUserId?: string | null;
}

/** A team member as returned by GET /api/v1/team (OWNER only — not in CREW_ALLOWED). */
interface TeamMember {
  id: string;
  email: string;
  displayName: string;
  role: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

/** The org shape returned by GET /api/v1/auth/session. */
interface OrgSession {
  organization: { id: string; name: string; timezone: string } | null;
}

const RESOLUTIONS: CalendarResolution[] = ['day', 'week', 'month'];

const WEEKDAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

/** Format a 'HH:MM' start time for display, or a fallback when unscheduled. */
function formatTime(time: string | null): string {
  if (!time) return 'Time TBD';
  return time;
}

/**
 * The Calendar view — day, week, or month.
 *
 * The visible range is computed by the tested helpers in @shared/calendar:
 *   rangeFor(resolution, anchor)  -> { start, end }
 *   gridFor(resolution, anchor)   -> the list of dates to render
 *   navigate(resolution, anchor, delta) -> move by one segment
 *   formatRangeLabel(resolution, anchor) -> the header label
 *
 * Jobs are fetched for the whole visible range in ONE request:
 *   GET /api/v1/jobs?from=<rangeFor().start>&to=<rangeFor().end>
 * then grouped by scheduledDate string on the client.
 *
 * "Today" and the initial anchor come from todayIn(orgTimezone) using the
 * session's timezone — never the browser's local date.
 */
export function CalendarPage() {
  const { user } = useSession();
  const isOwner = user?.role === 'OWNER';
  const [tz, setTz] = useState<string>('America/Chicago');
  const [resolution, setResolution] = useState<CalendarResolution>('week');
  const [anchor, setAnchor] = useState<string>('');
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  /** Map of userId -> displayName, for the assignee suffix (owner only). */
  const [teamNames, setTeamNames] = useState<Map<string, string>>(() => new Map());

  // Client-side navigation for the empty-day "add a job" controls. A raw
  // window.location.href assignment would discard the running SPA and boot
  // the whole shell again; useNavigate() keeps it client-side.
  const navigateTo = useNavigate();

  // Switch to day view anchored on a specific date (used by the "+N more"
  // control in month view).
  const showDay = useCallback((date: string) => {
    setResolution('day');
    setAnchor(date);
  }, []);

  // Resolve the organization timezone from the session endpoint so "today"
  // is always the org's calendar day, never the browser's.
  useEffect(() => {
    void (async () => {
      try {
        const session = await apiGet<OrgSession>('/api/v1/auth/session');
        if (session.organization?.timezone) {
          setTz(session.organization.timezone);
        }
      } catch {
        // Fall back to the default timezone; jobs will still load.
      }
    })();
  }, []);

  // Compute the initial anchor from the org timezone. Runs once on mount and
  // whenever the timezone is resolved.
  useEffect(() => {
    setAnchor(todayIn(tz));
  }, [tz]);

  // Fetch jobs for the visible range whenever the resolution or anchor changes.
  // ONE request per range change.
  const fetchJobs = useCallback(async () => {
    if (!anchor) return;
    const range = rangeFor(resolution, anchor);
    setLoading(true);
    setError(null);
    try {
      const data = await apiGet<Job[]>(`/api/v1/jobs?from=${range.start}&to=${range.end}`);
      setJobs(data);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not load the jobs.');
      setJobs(null);
    } finally {
      setLoading(false);
    }
  }, [resolution, anchor]);

  useEffect(() => {
    void fetchJobs();
  }, [fetchJobs]);

  // The assignee NAME needs the team list, and /team is OWNER-only (roles.ts
  // CREW_ALLOWED) — the same rule as the Today screen. One fetch, ever.
  useEffect(() => {
    if (!isOwner) return;
    let cancelled = false;
    void (async () => {
      try {
        const members = await apiGet<TeamMember[]>('/api/v1/team');
        if (cancelled) return;
        const map = new Map<string, string>();
        for (const member of members) {
          map.set(member.id, member.displayName);
        }
        setTeamNames(map);
      } catch {
        // Suffix is an enhancement; a failed team fetch is not fatal.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOwner]);

  const goToPrevious = useCallback(() => {
    if (!anchor) return;
    setAnchor(navigate(resolution, anchor, -1));
  }, [resolution, anchor]);

  const goToNext = useCallback(() => {
    if (!anchor) return;
    setAnchor(navigate(resolution, anchor, 1));
  }, [resolution, anchor]);

  const goToToday = useCallback(() => {
    setAnchor(todayIn(tz));
  }, [tz]);

  // Group jobs by scheduledDate string. A job appears on exactly one day, the
  // day the server says.
  //
  // Terminal-and-abandoned work stays off the calendar: CANCELLED never
  // happened and SKIPPED was deliberately not done, so neither belongs in the
  // day/week/month grids. COMPLETED still renders — finished work on its day
  // is history the owner wants to see. The filter is a typed guard, not a
  // string compare, so a status added to JOB_STATUSES later fails the build
  // here instead of silently leaking onto the calendar.
  const CALENDAR_STATUSES: readonly JobStatus[] = ['SCHEDULED', 'IN_PROGRESS', 'COMPLETED'];
  const isCalendarStatus = (status: string): status is JobStatus =>
    (CALENDAR_STATUSES as readonly string[]).includes(status);

  const byDate = new Map<string, Job[]>();
  if (jobs) {
    for (const job of jobs) {
      if (!isCalendarStatus(job.status)) continue;
      const bucket = byDate.get(job.scheduledDate);
      if (bucket) {
        bucket.push(job);
      } else {
        byDate.set(job.scheduledDate, [job]);
      }
    }
  }

  const today = todayIn(tz);
  const grid = anchor ? gridFor(resolution, anchor) : [];
  const label = anchor ? formatRangeLabel(resolution, anchor) : '';

  if (loading) {
    return (
      <main id="main" className={styles.container}>
        <p className={styles.status} aria-live="polite">
          Loading the {resolution}…
        </p>
      </main>
    );
  }

  if (error) {
    return (
      <main id="main" className={styles.container}>
        <p className={styles.status} role="alert">
          {error}
        </p>
        <button type="button" className={styles.retryButton} onClick={() => void fetchJobs()}>
          Retry
        </button>
      </main>
    );
  }

  return (
    <main id="main" className={styles.container}>
      <header className={styles.header}>
        <div
          className={styles.resolutionSwitcher}
          role="radiogroup"
          aria-label="Calendar resolution"
        >
          {RESOLUTIONS.map((res) => (
            <button
              key={res}
              type="button"
              role="radio"
              aria-checked={resolution === res}
              className={
                resolution === res ? styles.resolutionButtonActive : styles.resolutionButton
              }
              onClick={() => setResolution(res)}
            >
              {res.charAt(0).toUpperCase() + res.slice(1)}
            </button>
          ))}
        </div>

        <button
          type="button"
          className={styles.navButton}
          aria-label="Previous"
          onClick={goToPrevious}
        >
          ‹
        </button>
        <button type="button" className={styles.navButton} aria-label="Next" onClick={goToNext}>
          ›
        </button>
        <button
          type="button"
          className={styles.navButton}
          aria-label="Back to today"
          onClick={goToToday}
        >
          Today
        </button>
        <span className={styles.range} aria-label="Current range">
          {label}
        </span>
      </header>

      {resolution === 'day' && (
        <DayView
          date={grid[0] ?? ''}
          byDate={byDate}
          today={today}
          teamNames={teamNames}
          showAssignee={isOwner}
        />
      )}

      {resolution === 'week' && (
        <WeekView
          grid={grid}
          byDate={byDate}
          today={today}
          weekdayNames={WEEKDAY_NAMES}
          onAddJob={navigateTo}
          teamNames={teamNames}
          showAssignee={isOwner}
        />
      )}

      {resolution === 'month' && (
        <MonthView
          grid={grid}
          anchor={anchor}
          byDate={byDate}
          today={today}
          onAddJob={navigateTo}
          onShowDay={showDay}
          teamNames={teamNames}
          showAssignee={isOwner}
        />
      )}
    </main>
  );
}

/** Day view: a single column listing that day's jobs. */
function DayView({
  date,
  byDate,
  today,
  teamNames,
  showAssignee,
}: {
  date: string;
  byDate: Map<string, Job[]>;
  today: string;
  teamNames: Map<string, string>;
  showAssignee: boolean;
}) {
  const dayJobs = byDate.get(date) ?? [];
  const isToday = compareDates(date, today) === 0;

  return (
    <section
      className={isToday ? styles.dayColumnToday : styles.dayColumn}
      data-date={date}
      data-today={isToday ? 'true' : 'false'}
      role="gridcell"
      {...(isToday ? { 'data-testid': 'today-marker' } : {})}
    >
      <div className={styles.dayHeader}>
        <span className={styles.weekdayName}>{date}</span>
      </div>
      {dayJobs.length === 0 ? (
        <p className={styles.emptyMarker} aria-label={`No jobs on ${date}`}>
          No jobs
        </p>
      ) : (
        <ul className={styles.jobList} role="list">
          {dayJobs.map((job) => (
            <li key={job.id} className={styles.jobRow}>
              <Link to={`/jobs/${job.id}`} className={styles.jobLink}>
                <span className={styles.jobTitle}>
                  {job.title}
                  <AssigneeSuffix job={job} teamNames={teamNames} show={showAssignee} />
                </span>
                <span className={styles.jobTime}>{formatTime(job.scheduledStartTime)}</span>
                <span className={styles.jobStatus}>{job.status}</span>
                <span className={styles.jobPrice}>{formatCents(job.priceCents)}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * The " · Name" suffix after a job title. Rendered only when the caller may
 * know names at all (OWNER — /team is not in CREW_ALLOWED) and the job is
 * assigned; the gate is the ROLE, never the field's presence, exactly like
 * the money fields.
 */
function AssigneeSuffix({
  job,
  teamNames,
  show,
}: {
  job: Job;
  teamNames: Map<string, string>;
  show: boolean;
}) {
  if (!show || !job.assignedUserId) return null;
  const name = teamNames.get(job.assignedUserId);
  if (!name) return null;
  return <span className={styles.jobAssignee}>{` · ${name}`}</span>;
}

/** Week view: seven day columns, stacked into a list on narrow screens. */
function WeekView({
  grid,
  byDate,
  today,
  weekdayNames,
  onAddJob,
  teamNames,
  showAssignee,
}: {
  grid: string[];
  byDate: Map<string, Job[]>;
  today: string;
  weekdayNames: string[];
  onAddJob: (to: string) => void;
  teamNames: Map<string, string>;
  showAssignee: boolean;
}) {
  return (
    <ul className={styles.weekList} role="list">
      {grid.map((date, i) => {
        const dayJobs = byDate.get(date) ?? [];
        const isToday = compareDates(date, today) === 0;
        const weekday = weekdayNames[i] ?? '';

        return (
          <li
            key={date}
            className={isToday ? styles.dayColumnToday : styles.dayColumn}
            data-date={date}
            data-today={isToday ? 'true' : 'false'}
            {...(isToday ? { 'data-testid': 'today-marker' } : {})}
          >
            <div className={styles.dayHeader}>
              <span className={styles.weekdayName}>{weekday}</span>
              <span className={styles.dayDate}>{date}</span>
            </div>
            {dayJobs.length === 0 ? (
              <button
                type="button"
                className={styles.emptyDayButton}
                aria-label={`No jobs on ${date}. Add a job for ${date}.`}
                onClick={() => {
                  onAddJob(`/jobs/new?date=${date}`);
                }}
              >
                No jobs
              </button>
            ) : (
              <ul className={styles.jobList} role="list">
                {dayJobs.map((job) => (
                  <li key={job.id} className={styles.jobRow}>
                    <Link to={`/jobs/${job.id}`} className={styles.jobLink}>
                      <span className={styles.jobTitle}>
                        {job.title}
                        <AssigneeSuffix job={job} teamNames={teamNames} show={showAssignee} />
                      </span>
                      <span className={styles.jobTime}>{formatTime(job.scheduledStartTime)}</span>
                      <span className={styles.jobStatus}>{job.status}</span>
                      <span className={styles.jobPrice}>{formatCents(job.priceCents)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** Month view: a grid of complete weeks. */
function MonthView({
  grid,
  anchor,
  byDate,
  today,
  onAddJob,
  onShowDay,
  teamNames,
  showAssignee,
}: {
  grid: string[];
  anchor: string;
  byDate: Map<string, Job[]>;
  today: string;
  onAddJob: (to: string) => void;
  onShowDay: (date: string) => void;
  teamNames: Map<string, string>;
  showAssignee: boolean;
}) {
  return (
    <div className={styles.monthGrid} role="grid">
      {grid.map((date) => {
        const dayJobs = byDate.get(date) ?? [];
        const isToday = compareDates(date, today) === 0;
        const outside = isOutsideMonth(date, anchor);
        const dayNumber = date.slice(8, 10);

        return (
          <div
            key={date}
            className={[
              styles.monthCell,
              outside ? styles.monthCellOutside : '',
              isToday ? styles.monthCellToday : '',
            ].join(' ')}
            data-date={date}
            data-today={isToday ? 'true' : 'false'}
            data-outside={outside ? 'true' : 'false'}
            {...(isToday ? { 'data-testid': 'today-marker' } : {})}
            role="gridcell"
          >
            <span className={styles.monthCellDayNumber}>{dayNumber}</span>
            {dayJobs.length === 0 ? (
              <button
                type="button"
                className={styles.monthEmptyButton}
                aria-label={`No jobs on ${date}. Add a job for ${date}.`}
                onClick={() => {
                  onAddJob(`/jobs/new?date=${date}`);
                }}
              >
                +
              </button>
            ) : (
              <ul className={styles.monthJobList} role="list">
                {dayJobs.slice(0, 3).map((job) => (
                  <li key={job.id} className={styles.monthJobRow}>
                    <Link to={`/jobs/${job.id}`} className={styles.monthJobLink}>
                      <span className={styles.monthJobTitle}>
                        {job.title}
                        <AssigneeSuffix job={job} teamNames={teamNames} show={showAssignee} />
                      </span>
                      <span className={styles.monthJobTime}>
                        {formatTime(job.scheduledStartTime)}
                      </span>
                    </Link>
                  </li>
                ))}
                {dayJobs.length > 3 && (
                  <li
                    className={styles.monthMore}
                    aria-label={`+${dayJobs.length - 3} more`}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      onShowDay(date);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onShowDay(date);
                      }
                    }}
                  >
                    +{dayJobs.length - 3} more
                  </li>
                )}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}
